import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { ThemeLayout, ThemeManifest, ThemeTokens, SettingsValues } from "@workspace/theme-engine";
import { useI18n } from "@/lib/i18n";

/**
 * Storefront Secure Theme Engine context.
 *
 * Additive by design: the engine is ACTIVE only when the tenant has opted into
 * an engine theme (`/api/theme-engine/active` returns `enabled: true`). When
 * disabled — the default — this provider renders nothing and the legacy
 * storefront chrome is used unchanged. This protects production stores from any
 * behavioural change until a merchant explicitly activates an engine theme.
 *
 * The engine NEVER executes author JavaScript. The server already scopes +
 * sanitizes + branding-locks the theme CSS before it reaches us; we only apply
 * the resulting `--nx-*` variables and inject the already-safe CSS string.
 */
export interface ActiveEngineTheme {
  enabled: true;
  slug: string;
  version: string;
  manifest: ThemeManifest;
  layout: ThemeLayout;
  templates: Record<string, string>;
  tokens: ThemeTokens;
  vars: Record<string, string>;
  settings: SettingsValues;
  scope: string;
  css: string;
  /** True when this theme is an admin-only live preview, not the live store theme. */
  preview?: boolean;
}

/**
 * Query param an admin appends to a storefront URL to render a CANDIDATE theme
 * live on their real store data without activating it for shoppers. The preview
 * endpoint is admin-session-gated, so shoppers who land on such a URL get the
 * real active theme instead.
 */
export const THEME_PREVIEW_PARAM = "__nx_theme_preview";

type EngineState = ActiveEngineTheme | { enabled: false } | null;

const ThemeEngineContext = createContext<EngineState>(null);

/** The DOM id of the container the engine theme mounts inside. */
export const ENGINE_SCOPE_ID = "nx-theme-root";
const ENGINE_STYLE_ID = "nx-theme-engine-css";
const ENGINE_VARS_STYLE_ID = "nx-theme-engine-vars";

function injectStyle(id: string, css: string): void {
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!css) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("style");
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

/**
 * Convert a #hex colour to the "H S% L%" triplet that shadcn/Tailwind tokens
 * expect for `hsl(var(--primary))`-style consumption. Returns null for any
 * non-hex value (rgb()/hsl()/named) so the caller can skip the HSL var but
 * still set the raw `--theme-*` one.
 */
function hexToHsl(hex: string): string | null {
  const h = hex.trim();
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h)) return null;
  let r: number, g: number, b: number;
  if (h.length === 4) {
    r = parseInt(h[1] + h[1], 16);
    g = parseInt(h[2] + h[2], 16);
    b = parseInt(h[3] + h[3], 16);
  } else {
    r = parseInt(h.slice(1, 3), 16);
    g = parseInt(h.slice(3, 5), 16);
    b = parseInt(h.slice(5, 7), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const lum = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = lum > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) hue = ((b - r) / d + 2) / 6;
    else hue = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(lum * 100)}%`;
}

/** Black or white, whichever contrasts better with the given hex colour. */
function contrastText(hex: string): string {
  const h = hex.trim();
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h)) return "#ffffff";
  const full = h.length === 4 ? `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}` : h;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? "#000000" : "#ffffff";
}

/**
 * Bridge the compiled `--nx-*` engine tokens onto the LEGACY `--theme-*` /
 * shadcn HSL custom properties that the existing storefront chrome AND content
 * (header, footer, product cards, buttons, hero) were written against.
 *
 * Without this, an activated/previewed engine theme injects its `--nx-*`
 * variables but the store looks unchanged — nothing in the React storefront
 * reads `--nx-*`. The bridge is what makes "preview" and "activate" actually
 * recolour/restyle the store. Returned as declaration lines; the caller scopes
 * them to `#nx-theme-root` so they cascade over the storefront but never leak
 * into the admin panel chrome (rendered outside that root).
 */
function buildLegacyBridge(vars: Record<string, string>, ar: boolean): string {
  const lines: string[] = [];
  const set = (name: string, value: string | undefined | null) => {
    if (value) lines.push(`  ${name}: ${value};`);
  };
  const color = (key: string): string | undefined => vars[`--nx-color-${key}`];

  const primary = color("primary");
  if (primary) {
    set("--theme-primary", primary);
    set("--theme-btn-primary-bg", primary);
    set("--primary", hexToHsl(primary));
  }
  const accent = color("accent");
  if (accent) {
    set("--theme-accent", accent);
    set("--accent", hexToHsl(accent));
    // Accent drives the Add-to-Cart buttons storefront-wide (same contract
    // as the legacy appearance settings in theme-context.tsx).
    set("--theme-cart-btn-bg", accent);
    set("--theme-cart-btn-text", contrastText(accent));
  }
  const background = color("background");
  if (background) {
    set("--theme-bg", background);
    set("--background", hexToHsl(background));
  }
  const text = color("text");
  if (text) {
    set("--theme-text", text);
    set("--foreground", hexToHsl(text));
  }
  set("--theme-header-bg", color("header-bg"));
  set("--theme-header-text", color("header-text"));
  set("--theme-footer-bg", color("footer-bg"));
  set("--theme-footer-text", color("footer-text"));

  const fontAr = vars["--nx-font-ar"];
  const fontEn = vars["--nx-font-en"];
  const fontHeading = vars["--nx-font-heading"];
  set("--theme-font-ar", fontAr);
  set("--theme-font-en", fontEn);
  const bodyFamily = ar ? fontAr || fontEn : fontEn || fontAr;
  if (bodyFamily) {
    const stack = `"${bodyFamily}", sans-serif`;
    set("--app-font-sans", stack);
    set("--theme-body-font", stack);
  }
  const headingFamily = fontHeading || bodyFamily;
  if (headingFamily) set("--theme-heading-font", `"${headingFamily}", sans-serif`);

  const radiusButton = vars["--nx-radius-button"];
  set("--theme-card-radius", vars["--nx-radius-card"]);
  set("--theme-btn-primary-radius", radiusButton);
  set("--theme-banner-radius", vars["--nx-radius-banner"]);
  if (radiusButton) set("--radius", radiusButton);

  return lines.join("\n");
}

/** Bilingual, status-specific explanation for why a theme preview failed. */
function previewFailureReason(status: number | null, ar: boolean): string {
  if (status === 401)
    return ar
      ? "جلستك كمشرف غير محمولة على هذا النطاق (401). افتح رابط المعاينة من لوحة التحكم على نفس نطاق المتجر."
      : "Your admin session isn't carried on this domain (401). Open the preview from the admin panel on the same store domain.";
  if (status === 403)
    return ar
      ? "ميزة محرّك القوالب غير مفعّلة لباقتك الحالية (403)."
      : "The theme engine feature isn't enabled for your current plan (403).";
  if (status === 404)
    return ar
      ? "القالب المطلوب معاينته غير موجود (404)."
      : "The theme you tried to preview wasn't found (404).";
  if (status && status >= 500)
    return ar
      ? `حدث خطأ في الخادم أثناء تحميل المعاينة (${status}).`
      : `A server error occurred while loading the preview (${status}).`;
  if (status)
    return ar ? `تعذّر تحميل المعاينة (${status}).` : `Couldn't load the preview (${status}).`;
  return ar
    ? "تعذّر تحميل المعاينة (خطأ في الشبكة أو القالب غير مُفعّل)."
    : "Couldn't load the preview (network error or the theme is not enabled).";
}

export function ThemeEngineProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<EngineState>(null);
  // When an admin opens a `?__nx_theme_preview=` link and the preview cannot be
  // loaded, we surface WHY instead of silently falling back. Only ever set when
  // the preview param is present, so shoppers on a normal URL never see it.
  const [previewError, setPreviewError] = useState<{ status: number | null } | null>(null);

  useEffect(() => {
    const BASE_URL = import.meta.env.BASE_URL || "/";
    let cancelled = false;

    // An admin can request a live preview of a candidate theme via a query
    // param. The preview endpoint is admin-session-gated; if it fails (e.g. a
    // shopper opened the link, or the session expired) we fall back to the real
    // active theme so shoppers never see an unintended preview.
    const previewSlug =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get(THEME_PREVIEW_PARAM)
        : null;

    const loadActive = () =>
      fetch(`${BASE_URL}api/theme-engine/active`, { credentials: "include" })
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setState(data && data.enabled ? (data as ActiveEngineTheme) : { enabled: false });
        })
        .catch(() => {
          if (!cancelled) setState({ enabled: false });
        });

    if (previewSlug) {
      fetch(`${BASE_URL}api/theme-engine/preview/${encodeURIComponent(previewSlug)}`, {
        credentials: "include",
      })
        .then(async (r) => {
          const data = r.ok ? await r.json().catch(() => null) : null;
          return { status: r.status, ok: r.ok, data };
        })
        .then(({ status, ok, data }) => {
          if (cancelled) return;
          if (ok && data && data.enabled) {
            setPreviewError(null);
            setState({ ...(data as ActiveEngineTheme), preview: true });
          } else {
            // Surface WHY the preview failed instead of silently swapping in
            // the live theme with no explanation. This is gated on the preview
            // param being present (only an admin appends it), so shoppers on a
            // normal URL never see it. We DELIBERATELY surface the 401 case too:
            // when an admin's session cookie isn't carried onto a custom domain,
            // the admin and a shopper are indistinguishable client-side, and the
            // "session not on this domain" hint is the single most useful
            // diagnostic — suppressing it would hide the exact failure we are
            // fixing. `ok` but not enabled → the theme exists yet isn't a usable
            // engine theme, so report it as a generic (status-less) failure.
            setPreviewError({ status: ok ? null : status });
            void loadActive();
          }
        })
        .catch(() => {
          if (cancelled) return;
          setPreviewError({ status: null });
          void loadActive();
        });
    } else {
      void loadActive();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!state || !state.enabled) {
      injectStyle(ENGINE_STYLE_ID, "");
      injectStyle(ENGINE_VARS_STYLE_ID, "");
      return;
    }
    // CSS variables are scoped to the engine root selector so they never leak
    // into platform chrome rendered outside the engine container. We emit the
    // raw `--nx-*` tokens AND a bridge onto the legacy `--theme-*`/shadcn vars
    // that the existing storefront actually consumes — otherwise activating or
    // previewing an engine theme injects variables nothing reads, so the store
    // looks unchanged.
    const ar = typeof document !== "undefined" ? document.documentElement.dir !== "ltr" : true;
    const engineBody = Object.entries(state.vars)
      .map(([k, v]) => `  ${k}: ${v};`)
      .join("\n");
    const bridgeBody = buildLegacyBridge(state.vars, ar);
    const body = [engineBody, bridgeBody].filter(Boolean).join("\n");
    const varsCss = body ? `#${ENGINE_SCOPE_ID} {\n${body}\n}` : "";
    injectStyle(ENGINE_VARS_STYLE_ID, varsCss);
    injectStyle(ENGINE_STYLE_ID, state.css || "");
  }, [state]);

  const previewing = !!(state && state.enabled && state.preview);

  return (
    <ThemeEngineContext.Provider value={state}>
      {previewing && <ThemePreviewBanner themeName={(state as ActiveEngineTheme).manifest?.name} />}
      {previewError && <ThemePreviewErrorBanner status={previewError.status} />}
      {children}
    </ThemeEngineContext.Provider>
  );
}

/**
 * Admin-only fixed banner shown when a `?__nx_theme_preview=` link could NOT be
 * applied. Replaces the old silent fallback so the admin sees exactly why
 * (401/403/404/…). Only rendered when the preview param was present, so normal
 * shoppers never encounter it. Bilingual + RTL-aware.
 */
function ThemePreviewErrorBanner({ status }: { status: number | null }) {
  const { lang } = useI18n();
  const ar = lang === "ar";

  const dismiss = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(THEME_PREVIEW_PARAM);
    window.location.href = url.toString();
  };

  return (
    <div
      dir={ar ? "rtl" : "ltr"}
      data-testid="theme-preview-error-banner"
      style={{
        position: "fixed",
        top: 0,
        insetInlineStart: 0,
        insetInlineEnd: 0,
        zIndex: 2147483647,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        flexWrap: "wrap",
        padding: "0.5rem 1rem",
        background: "#7f1d1d",
        color: "#fef2f2",
        fontFamily: "system-ui, sans-serif",
        fontSize: "0.875rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
        <span aria-hidden style={{ fontSize: "1rem" }}>⚠️</span>
        <strong>{ar ? "تعذّر تطبيق المعاينة" : "Preview couldn't be applied"}</strong>
        <span style={{ opacity: 0.92 }}>{previewFailureReason(status, ar)}</span>
      </span>
      <button
        type="button"
        onClick={dismiss}
        data-testid="button-dismiss-theme-preview-error"
        style={{
          border: "1px solid rgba(254,242,242,0.4)",
          background: "transparent",
          color: "#fef2f2",
          borderRadius: "9999px",
          padding: "0.2rem 0.85rem",
          fontSize: "0.8rem",
          cursor: "pointer",
        }}
      >
        {ar ? "إغلاق" : "Dismiss"}
      </button>
    </div>
  );
}

/**
 * Admin-only fixed banner shown while a candidate theme is being previewed live
 * on the merchant's real store. Reinforces that this is NOT the live theme and
 * offers a one-click exit back to the real store. Bilingual + RTL-aware.
 */
function ThemePreviewBanner({ themeName }: { themeName?: { ar: string; en: string } }) {
  const { lang } = useI18n();
  const ar = lang === "ar";
  const name = themeName ? (ar ? themeName.ar : themeName.en) : "";

  const exitPreview = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(THEME_PREVIEW_PARAM);
    window.location.href = url.toString();
  };

  return (
    <div
      dir={ar ? "rtl" : "ltr"}
      data-testid="theme-preview-banner"
      style={{
        position: "fixed",
        top: 0,
        insetInlineStart: 0,
        insetInlineEnd: 0,
        zIndex: 2147483647,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        flexWrap: "wrap",
        padding: "0.5rem 1rem",
        background: "#0f172a",
        color: "#f8fafc",
        fontFamily: "system-ui, sans-serif",
        fontSize: "0.875rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
        <span aria-hidden style={{ fontSize: "1rem" }}>👁️</span>
        <strong>{ar ? "وضع المعاينة" : "Preview mode"}</strong>
        <span style={{ opacity: 0.85 }}>
          {ar
            ? `أنت تعاين «${name}» على بيانات متجرك الحقيقية — لم يتم تفعيله للزوار بعد.`
            : `Previewing "${name}" on your real store data — not live for shoppers yet.`}
        </span>
      </span>
      <button
        type="button"
        onClick={exitPreview}
        data-testid="button-exit-theme-preview"
        style={{
          border: "1px solid rgba(248,250,252,0.4)",
          background: "transparent",
          color: "#f8fafc",
          borderRadius: "9999px",
          padding: "0.2rem 0.85rem",
          fontSize: "0.8rem",
          cursor: "pointer",
        }}
      >
        {ar ? "إنهاء المعاينة" : "Exit preview"}
      </button>
    </div>
  );
}

/** Returns the active engine theme, or null when the engine is disabled. */
export function useThemeEngine(): ActiveEngineTheme | null {
  const state = useContext(ThemeEngineContext);
  return state && state.enabled ? state : null;
}
